import React from 'react';
import { useNavigate } from 'react-router-dom';

interface WhyPartnerSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const WhyPartnerSection: React.FC<WhyPartnerSectionProps> = ({ highlightColor }) => {
    const navigate = useNavigate();
    const stats = [
        { value: 'Visibility', label: 'Daily updates across all connected wearables and testing tools.' },
        { value: 'Quality ', label: 'Filtered data that removes noise and incomplete sessions.' },
        { value: 'Profiles', label: 'Unified training, recovery, and readiness information for every athlete.' },
        { value: 'Insights', label: 'Team-specific trends that match your schedule and training approach.' },
        { value: 'Clarity', label: 'Simple signals that support confident coaching decisions.' },
        { value: 'Security', label: 'NCAA-ready with HIPAA and FERPA support for all athlete data.' }
    ];

    const features = [
        'Unify every data source your athletes already use, including WHOOP, Oura, Garmin, VALD Nord Bords, Hawkins Force Plates, etc.',
        'Filter and clean data automatically so you only see reliable information that reflects actual athlete behavior and training load.',
        'View recovery, readiness, and workload trends in one place to support daily planning and long-term decision-making.'
    ];

    return (
        <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-black relative">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center observe-fade opacity-0">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Why Choose CGM Sports?
                        </h2>
                        <p className="text-gray-300 mb-6">
                            CGM Sports uses advanced AI to turn data from multiple wearable devices into clear, actionable insights for coaches and athletes.
                        </p>
                        <ul className="space-y-3 text-gray-300 mb-6">
                            {features.map((feature, index) => (
                                <li key={index} className="flex items-start">
                                    <span className="text-green-400 mr-3 text-xl">•</span>
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-gray-300 mb-6">
                            Use a platform that adapts to your training philosophy rather than forcing a one-size-fits-all model.
                        </p>
                        <p className="text-gray-300">
                            CGM gives teams a clearer picture of what their athletes are experiencing so coaches can make more informed adjustments.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-4xl md:text-5xl font-bold mb-2">
                                    <span 
                                        className="bg-clip-text text-transparent font-bold"
                                        style={{
                                            backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        {stat.value}
                                    </span>
                                </div>
                                <div className="text-gray-300 text-sm font-medium">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyPartnerSection;

